import { Heading } from '~/components/ui';
import Image from 'next/image';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';

import { removeHTMLTags } from '~/lib/util';

interface DealProps {
  id: string | number;
  public_name: string;
  media_url: string;
  sku: {
    price: number;
  };
  story: {
    card_description: string;
    highlights: { highlight: string }[];
    snippet?: string;
  };
  common_features?: { text: string }[];
  features?: { feature: string }[];
}

interface AlternativeCollectionProps {
  data_source_object: DealProps;
  deals: DealProps[];
  title: string;
  subheader: string;
  description: string;
}

const FEATURE_LIMIT = 4;
const ALTERNATIVE_LIMIT = 5;

// Split the description text by line breaks, select the first line, and remove any HTML tags
const formatDescriptionText = (text?: string): string => {
  if (!text) return '';

  const splitLines = text.split(/<br\s*\/?>|\r?\n|\r|\n/g);

  return removeHTMLTags(splitLines[0]);
};

const Features = ({ deal }: { deal: DealProps }) => {
  const featureList = useMemo(() => {
    if (deal.common_features?.length) {
      return deal.common_features
        .slice(0, FEATURE_LIMIT)
        .map((feature: { text: string }) => feature.text);
    } else if (deal.features?.length) {
      return deal.features
        .slice(0, FEATURE_LIMIT)
        .map((feature: { feature: string }) => feature.feature);
    } else if (deal.story?.highlights?.length) {
      return deal.story.highlights
        .slice(0, FEATURE_LIMIT)
        .map((feature: { highlight: string }) => feature.highlight);
    }

    return [];
  }, [deal]);

  return (
    <ul className="flex flex-col gap-y-2 text-sm sm:text-base">
      {featureList.map((feature: string) => (
        <li className="flex items-center gap-x-3 sm:gap-x-4" key={feature}>
          <FontAwesomeIcon
            icon={faCheck}
            className="mt-1 text-sm text-green-500 sm:text-2xl"
          />{' '}
          <div className="line-clamp-2">{feature}</div>
        </li>
      ))}
    </ul>
  );
};

export const Alternative = ({
  collection,
}: {
  collection: AlternativeCollectionProps;
}) => {
  const { data_source_object: deal } = collection;
  const { deals = [] } = collection;
  const [now] = useState(new Date());

  if (!deal) return <></>;

  return (
    <div className="relative text-left">
      {/* break out of parent container and go full width */}
      <div className="appsumo-breakout relative -mt-6 bg-black-pearl text-left text-white">
        <div className="mx-auto flex p-4 sm:p-6 lg:max-w-screen-4xl lg:gap-x-6 lg:py-8 lg:px-16 2xl:px-8">
          <div className="flex flex-col gap-y-6 lg:w-4/5">
            <Heading.H1 className="text-white lg:text-[42px] lg:leading-none">
              {collection.title}
            </Heading.H1>

            <div className="flex flex-col rounded-t sm:gap-y-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-x-10">
                <div className="relative sm:w-2/4 lg:w-1/3">
                  <Image
                    src={`${deal.media_url}?width=320&height=180`}
                    width={320}
                    height={180}
                    unoptimized
                    alt={deal.public_name}
                    className="aspect-video w-full rounded-t object-cover sm:rounded-b"
                  />
                  <Heading.H3 className="absolute bottom-0 w-full bg-black-pearl p-4 font-header text-2xl font-semibold text-white opacity-90 sm:hidden">
                    {deal.public_name}
                  </Heading.H3>
                </div>
                <div className="pb-4 pt-2 max-sm:border-x max-sm:border-spruce sm:w-2/4 sm:pt-0 lg:w-2/3">
                  <Heading.H2 className="hidden font-header font-semibold text-white ">
                    {deal.public_name}
                  </Heading.H2>
                  <p className="break-words text-sm text-white line-clamp-3 max-sm:px-4 sm:my-2 sm:text-lg sm:font-semibold md:line-clamp-5">
                    {formatDescriptionText(deal?.story?.snippet)}
                  </p>
                </div>
              </div>
              <dl className="flex flex-col justify-between gap-y-4 rounded-b max-sm:border-x max-sm:border-b max-sm:border-spruce max-sm:px-4 max-sm:pb-4 sm:flex-row sm:items-center">
                <Features deal={deal} />
              </dl>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="rounded border border-grace p-4">
              <p className="font-header text-lg font-semibold ">
                {collection.subheader}
              </p>
              <ul className="mt-4 ml-6 flex list-outside list-disc flex-col gap-y-2">
                {deals.slice(0, ALTERNATIVE_LIMIT).map((deal: DealProps) => (
                  <li key={deal.id}>{deal.public_name}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8">
        <Heading.H2>
          Top {deals.length} Alternatives to {deal.public_name} in{' '}
          {now.getFullYear()}
        </Heading.H2>
        <div
          className="mt-1 max-w-2xl text-sm md:text-base"
          dangerouslySetInnerHTML={{ __html: collection.description }}
        />
      </div>
      <div className="my-4 border-t border-sundog md:hidden" />
    </div>
  );
};
